'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var _rollupPluginBabelHelpers = require('../_virtual/_rollupPluginBabelHelpers.js');
var React = require('react');
require('../node_modules/prop-types/index.js');
var LinearProgress = require('@material-ui/core/LinearProgress');
var getCellAlignment = require('./utils/getCellAlignment.js');
var useDebounce = require('../useDebounce/useDebounce.js');
var CircleLoader = require('../CircleLoader/CircleLoader.js');
var DragBoundry = require('./DragBoundry.js');
var DataWindow = require('./DataWindow.js');
var Header = require('./Header.js');
var Totals = require('./Totals.js');
var calculateWidths = require('./utils/calculateWidths.js');
var math = require('../utils/math.js');
var scrollbarSize = require('../node_modules/dom-helpers/esm/scrollbarSize.js');
var DataTableContext = require('./DataTableContext.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

function _interopNamespace(e) {
  if (e && e.__esModule) return e;
  var n = Object.create(null);
  if (e) {
    Object.keys(e).forEach(function (k) {
      if (k !== 'default') {
        var d = Object.getOwnPropertyDescriptor(e, k);
        Object.defineProperty(n, k, d.get ? d : {
          enumerable: true,
          get: function () {
            return e[k];
          }
        });
      }
    });
  }
  n['default'] = e;
  return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespace(React);
var LinearProgress__default = /*#__PURE__*/_interopDefaultLegacy(LinearProgress);

var HEADER_HEIGHT = 56;
var TOTALS_HEIGHT = 56;
var HEIGHT_BUFFER = 7;
var INNER_BORDER_WIDTH = 2;
var DataTable = _ref => {
  var {
    actionsWidth = 0,
    columns: columnsFromProps = [],
    containerHeight = 0,
    containerWidth = 0,
    customRenderers,
    isFetching = false,
    isLoading = false,
    maxColumnWidth = 500,
    minColumnWidth = 50,
    onColumnResize,
    onItemsRendered,
    overscanCount = 30,
    renderActions,
    rowCount,
    rowHeight = 44,
    rows = {},
    selected,
    selectionMode,
    selectMany,
    setSortBy,
    sortBy,
    toggleSelected,
    toggleSelectionMode,
    totals,
    unselectMany
  } = _ref;
  var [columns, setColumns] = React__namespace.useState([]);
  var [rowWindow, setRowWindow] = React__namespace.useState({
    top: 0,
    bottom: 0,
    visibleCount: 0
  }); // --- Dimesions

  var dataGridHeight = containerHeight - HEADER_HEIGHT - TOTALS_HEIGHT - HEIGHT_BUFFER - 2 * INNER_BORDER_WIDTH;
  var hasScrollbar = rowCount >= rowWindow.visibleCount;
  var scrollbarWidth = hasScrollbar ? scrollbarSize() : 0; // --- Selections

  var [lastSelection, setLastSelection] = React__namespace.useState(null);
  var toggleSelectRow = React__namespace.useCallback((event, value, index) => {
    var isSelected = selectionMode === 'include' ? selected.has(value) : !selected.has(value);

    if (event.shiftKey && lastSelection !== null) {
      var bottom = Math.min(lastSelection.index, index);
      var top = Math.max(lastSelection.index, index);
      var values = [];

      for (var i = bottom; i <= top; i += 1) {
        var _rows$i;

        if ((_rows$i = rows[i]) !== null && _rows$i !== void 0 && _rows$i.id) {
          values.push(rows[i].id);
        }
      }

      if (lastSelection.isSelected) {
        selectMany(values);
      } else {
        unselectMany(values);
      }

      setLastSelection(null);
      return;
    }

    toggleSelected(value);
    setLastSelection({
      index,
      isSelected: selectionMode === 'include' ? !isSelected : isSelected
    });
  }, [selected, lastSelection, selectionMode]);
  var calculateRowWindow = React__namespace.useCallback(scrollPosition => {
    var visibleTopRow = Math.ceil(scrollPosition / rowHeight);
    var topRow = Math.max(0, visibleTopRow - overscanCount);
    var visibleCount = Math.floor(dataGridHeight / rowHeight);
    var visibleBottomRow = visibleTopRow + visibleCount;
    var bottomRow = Math.min(rowCount - 1, visibleBottomRow + overscanCount);
    onItemsRendered === null || onItemsRendered === void 0 ? void 0 : onItemsRendered({
      topRow,
      bottomRow
    });
    setRowWindow({
      topRow,
      bottomRow,
      visibleCount
    });
  }, [dataGridHeight, rowCount, onItemsRendered, rowHeight]);
  React__namespace.useEffect(() => {
    calculateRowWindow(0);
  }, [isLoading]); // --- Scrolling

  var onScroll = React__namespace.useCallback(event => {
    var {
      clientHeight,
      scrollHeight,
      scrollTop
    } = event.currentTarget;
    var scrollPosition = Math.max(0, Math.min(scrollTop, scrollHeight - clientHeight));
    calculateRowWindow(scrollPosition);
  }, [calculateRowWindow]);
  var debouncedRowWindow = useDebounce.useDebounce(rowWindow, 50); // Synchronize changes in the columns from props.
  // Additionally: add alignment and sorting info onto the columns.

  React__namespace.useEffect(() => {
    var widths = calculateWidths.calculateWidths({
      widths: columnsFromProps.map(column => {
        var _column$width;

        return (_column$width = column === null || column === void 0 ? void 0 : column.width) !== null && _column$width !== void 0 ? _column$width : 0;
      }),
      minWidth: minColumnWidth,
      maxWidth: maxColumnWidth,
      containerWidth,
      actionsWidth: actionsWidth,
      checkboxWidth: rowHeight
    });
    setColumns(columnsFromProps.map((column, index) => _rollupPluginBabelHelpers.objectSpread2(_rollupPluginBabelHelpers.objectSpread2({}, column), {}, {
      align: getCellAlignment.getCellAlignment(column.type),
      width: widths[index]
    })));
  }, [columnsFromProps, containerWidth, sortBy]);
  var autoSizeColumns = React__namespace.useCallback(() => {
    var newWidth = Math.max(0, math.safeDivide(containerWidth - rowHeight - actionsWidth, columns.length));
    onColumnResize('__ALL__', newWidth);
  }, [containerWidth, columns]);
  var [dragInfo, setDragInfo] = React__namespace.useState(null);
  var startResizing = React__namespace.useCallback((event, columnIndex) => {
    var _columns$columnIndex$, _columns$columnIndex;

    var left = columns.slice(0, columnIndex).reduce((total, column) => total + column.width, rowHeight);
    setDragInfo({
      columnIndex,
      left,
      initialX: event.clientX,
      width: (_columns$columnIndex$ = columns === null || columns === void 0 ? void 0 : (_columns$columnIndex = columns[columnIndex]) === null || _columns$columnIndex === void 0 ? void 0 : _columns$columnIndex.width) !== null && _columns$columnIndex$ !== void 0 ? _columns$columnIndex$ : 0
    });
  }, [columns]);
  var stopResizing = React__namespace.useCallback(currentX => {
    if (dragInfo) {
      var {
        width,
        initialX
      } = dragInfo;
      var newWidth = Math.min(maxColumnWidth, Math.max(minColumnWidth, width + (currentX - initialX)));
      onColumnResize(dragInfo.columnIndex, newWidth);
      setDragInfo(null);
    }
  }, [dragInfo]);
  return /*#__PURE__*/React__namespace.createElement(DataTableContext.DataTableContext.Provider, {
    value: {
      startResizing,
      isResizing: Boolean(dragInfo),
      columns,
      rows,
      selected,
      selectionMode,
      toggleSelected,
      toggleSelectionMode,
      toggleSelectRow,
      sortBy,
      setSortBy,
      customRenderers,
      renderActions
    }
  }, /*#__PURE__*/React__namespace.createElement("div", {
    className: "GenjoDataTable__root"
  }, /*#__PURE__*/React__namespace.createElement(Header.Header, {
    isLoading: isLoading,
    columns: columns,
    selected: selected,
    rowHeight: rowHeight,
    selectionMode: selectionMode,
    toggleSelectionMode: toggleSelectionMode,
    autoSizeColumns: autoSizeColumns,
    actionsWidth: actionsWidth
  }), isFetching && !isLoading && /*#__PURE__*/React__namespace.createElement("div", {
    className: "GenjoDataTable__fetch-bar",
    style: {
      top: rowHeight - 4
    }
  }, /*#__PURE__*/React__namespace.createElement(LinearProgress__default['default'], null)), isLoading ? /*#__PURE__*/React__namespace.createElement("div", {
    className: "GenjoDataTable__loading-container",
    style: {
      height: dataGridHeight,
      minHeight: dataGridHeight,
      maxHeight: dataGridHeight
    }
  }, /*#__PURE__*/React__namespace.createElement(CircleLoader.CircleLoader, {
    size: 64
  })) : rowCount === 0 ? /*#__PURE__*/React__namespace.createElement("div", {
    className: "GenjoDataTable__loading-container",
    style: {
      height: dataGridHeight,
      minHeight: dataGridHeight,
      maxHeight: dataGridHeight
    }
  }, /*#__PURE__*/React__namespace.createElement("span", null, "No results found")) : /*#__PURE__*/React__namespace.createElement("div", {
    className: "GenjoDataTable__data-grid",
    style: {
      height: dataGridHeight,
      maxHeight: dataGridHeight,
      minHeight: dataGridHeight
    },
    onScroll: onScroll
  }, /*#__PURE__*/React__namespace.createElement(DataWindow.DataWindow, _rollupPluginBabelHelpers['extends']({
    rowCount: rowCount,
    rowHeight: rowHeight,
    height: dataGridHeight,
    actionsWidth: actionsWidth,
    scrollbarWidth: scrollbarWidth
  }, debouncedRowWindow))), /*#__PURE__*/React__namespace.createElement(Totals.Totals, {
    columns: columns,
    totals: totals,
    actionsWidth: actionsWidth
  }), Boolean(dragInfo) && /*#__PURE__*/React__namespace.createElement(DragBoundry.DragBoundry, _rollupPluginBabelHelpers['extends']({}, dragInfo, {
    onMouseUp: stopResizing
  }))));
};
DataTable.propTypes = {};

exports.DataTable = DataTable;
exports.default = DataTable;
